import React,{useState,useEffect} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import useDashboardStore from '../store/dashboardStore';
import {formatCurrency,formatUnits,formatPercent} from '../utils/formatters';

const {FiX,FiDownload,FiSearch,FiChevronDown}=FiIcons;

const EmptyDrillState=()=> (
  <div className="flex-1 flex items-center justify-center">
    <div className="text-center">
      <div className="w-16 h-16 mx-auto mb-4 bg-brand-muted2/20 rounded-lg flex items-center justify-center">
        <svg className="w-8 h-8 text-brand-muted2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2h2a2 2 0 002-2z" />
        </svg>
      </div>
      <h4 className="text-brand-muted text-lg font-medium mb-2">No Detail Data</h4>
      <p className="text-brand-muted2 text-sm">Upload sales data to view detailed transaction records</p>
    </div>
  </div>
);

const DrillDrawer=()=> {
  const {drillDrawerOpen,toggleDrillDrawer,hasData,getDrillData}=useDashboardStore();
  const [searchTerm,setSearchTerm]=useState('');
  const [sortColumn,setSortColumn]=useState('txn_date');
  const [sortDirection,setSortDirection]=useState('desc');
  const [drillData,setDrillData]=useState([]);
  const [loading,setLoading]=useState(false);

  // Load drill data when drawer opens
  useEffect(()=> {
    if (drillDrawerOpen && hasData) {
      loadDrillData();
    }
  },[drillDrawerOpen,hasData]);

  const loadDrillData=async ()=> {
    setLoading(true);
    try {
      const data=await getDrillData();
      setDrillData(data);
    } catch (error) {
      console.error('Error loading drill data:',error);
      setDrillData([]);
    } finally {
      setLoading(false);
    }
  };

  const columns=[
    {key: 'txn_date',label: 'Date',width: 'w-24'},
    {key: 'product_name',label: 'Product',width: 'w-48'},
    {key: 'category',label: 'Category',width: 'w-24'},
    {key: 'department',label: 'Department',width: 'w-28'},
    {key: 'gross_sales',label: 'Gross Sales',width: 'w-24',formatter: formatCurrency},
    {key: 'discount_amt',label: 'Discount',width: 'w-20',formatter: formatCurrency},
    {key: 'net_sales',label: 'Net Sales',width: 'w-24',formatter: formatCurrency},
    {key: 'units',label: 'Units',width: 'w-16',formatter: formatUnits},
    {key: 'margin_pct',label: 'Margin %',width: 'w-20',formatter: formatPercent},
    {key: 'profit',label: 'Profit',width: 'w-20',formatter: formatCurrency},
    {key: 'cogs',label: 'COGS',width: 'w-20',formatter: formatCurrency}
  ];

  const filteredData=drillData.filter(row=>
    Object.values(row).some(value=>
      value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const sortedData=[...filteredData].sort((a,b)=> {
    const aVal=a[sortColumn];
    const bVal=b[sortColumn];
    const direction=sortDirection==='asc' ? 1 : -1;

    if (typeof aVal==='number' && typeof bVal==='number') {
      return (aVal - bVal) * direction;
    }
    return (aVal || '').toString().localeCompare((bVal || '').toString()) * direction;
  });

  const handleSort=(column)=> {
    if (sortColumn===column) {
      setSortDirection(sortDirection==='asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const exportCSV=()=> {
    if (!hasData || sortedData.length === 0) return;
    
    const headers=columns.map(col=> col.label).join(',');
    const rows=sortedData.map(row=>
      columns.map(col=> {
        const value=row[col.key];
        return col.formatter ? col.formatter(value) : (value || '');
      }).join(',')
    );
    const csvContent=[headers,...rows].join('\n');
    const blob=new Blob([csvContent],{type: 'text/csv'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url;
    a.download=`brioche-sales-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <AnimatePresence>
      {drillDrawerOpen && (
        <motion.div
          className="fixed inset-0 bg-black/50 flex items-end justify-center z-50"
          initial={{opacity: 0}}
          animate={{opacity: 1}}
          exit={{opacity: 0}}
          onClick={toggleDrillDrawer}
        >
          <motion.div
            className="bg-brand-card rounded-t-card w-full max-w-7xl h-3/4 flex flex-col shadow-card"
            initial={{y: '100%'}}
            animate={{y: 0}}
            exit={{y: '100%'}}
            transition={{type: 'spring',damping: 25,stiffness: 300}}
            onClick={(e)=> e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-brand-muted2/20">
              <div>
                <h3 className="text-lg font-semibold text-brand-fg">Drill-to-Details</h3>
                <p className="text-sm text-brand-muted">
                  {loading ? 'Loading...' : `${sortedData.length} rows (includes zero values)`}
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Search - only show if we have data */}
                {hasData && !loading && (
                  <div className="relative">
                    <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-muted" />
                    <input
                      type="text"
                      placeholder="Search..."
                      value={searchTerm}
                      onChange={(e)=> setSearchTerm(e.target.value)}
                      className="bg-brand-muted2/10 text-brand-fg rounded-button pl-10 pr-4 py-2 text-sm border-none outline-none w-64"
                    />
                  </div>
                )}

                {/* Export - only show if we have data */}
                {hasData && !loading && (
                  <motion.button
                    onClick={exportCSV}
                    className="flex items-center space-x-2 bg-brand-accent hover:bg-brand-accent/80 text-brand-bg rounded-button px-4 py-2 text-sm transition-colors"
                    whileHover={{scale: 1.02}}
                    whileTap={{scale: 0.98}}
                  >
                    <SafeIcon icon={FiDownload} className="w-4 h-4" />
                    <span>Export CSV</span>
                  </motion.button>
                )}

                {/* Close */}
                <motion.button
                  onClick={toggleDrillDrawer}
                  className="p-2 hover:bg-brand-muted2/20 rounded-button text-brand-muted hover:text-brand-fg transition-colors"
                  whileHover={{scale: 1.05}}
                  whileTap={{scale: 0.95}}
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </motion.button>
              </div>
            </div>

            {/* Content */}
            {!hasData ? (
              <EmptyDrillState />
            ) : loading ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-brand-muted">Loading detailed data...</p>
                </div>
              </div>
            ) : (
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-brand-card border-b border-brand-muted2/20">
                    <tr>
                      {columns.map((column)=> (
                        <th
                          key={column.key}
                          className={`${column.width} text-left p-3 text-sm font-medium text-brand-muted cursor-pointer hover:text-brand-fg transition-colors`}
                          onClick={()=> handleSort(column.key)}
                        >
                          <div className="flex items-center space-x-1">
                            <span>{column.label}</span>
                            {sortColumn===column.key && (
                              <motion.div
                                initial={{rotate: 0}}
                                animate={{rotate: sortDirection==='desc' ? 180 : 0}}
                                transition={{duration: 0.2}}
                              >
                                <SafeIcon icon={FiChevronDown} className="w-3 h-3" />
                              </motion.div>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.map((row,index)=> (
                      <motion.tr
                        key={row.id || index}
                        className={`border-b border-brand-muted2/10 hover:bg-brand-muted2/5 transition-colors ${
                          (row.net_sales || 0) === 0 || (row.units || 0) === 0 ? 'opacity-60' : ''
                        }`}
                        initial={{opacity: 0,y: 10}}
                        animate={{opacity: 1,y: 0}}
                        transition={{duration: 0.1,delay: Math.min(index * 0.01,0.5)}}
                      >
                        {columns.map((column)=> (
                          <td key={column.key} className="p-3 text-sm text-brand-fg">
                            {column.formatter ? column.formatter(row[column.key]) : (row[column.key] || '—')}
                          </td>
                        ))}
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DrillDrawer;